import gameService from './gameService';

// Dynamically determine WebSocket URL based on current location
const getWebSocketURL = () => {
  if (process.env.REACT_APP_WS_URL) {
    return process.env.REACT_APP_WS_URL;
  }

  // In development mode, use localhost:8080 (backend server)
  if (process.env.NODE_ENV === 'development') {
    return 'ws://localhost:8080/ws';
  }

  // In production, use current window location to build WebSocket URL
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  return `${protocol}//${host}/ws`;
};

const WS_URL = getWebSocketURL();

// Message types
const MSG_TYPES = {
  // Client to Server
  CREATE_ROOM: 'CREATE_ROOM',
  JOIN_ROOM: 'JOIN_ROOM',
  REJOIN_SESSION: 'REJOIN_SESSION',
  START_GAME: 'START_GAME',
  SELECT_CARDS: 'SELECT_CARDS',
  ATTACK: 'ATTACK',
  CHANGE_DEFENSE: 'CHANGE_DEFENSE',
  LEAVE_ROOM: 'LEAVE_ROOM',
  KICK_PLAYER: 'KICK_PLAYER',
  ADD_AI_PLAYER: 'ADD_AI_PLAYER',
  REMOVE_AI_PLAYER: 'REMOVE_AI_PLAYER',
  SEND_MESSAGE: 'SEND_MESSAGE',
  PING: 'PING',  // Added for heartbeat

  // Server to Client
  SESSION_CREATED: 'SESSION_CREATED',
  ROOM_CREATED: 'ROOM_CREATED',
  PLAYER_JOINED: 'PLAYER_JOINED',
  PLAYER_LEFT: 'PLAYER_LEFT',
  PLAYER_KICKED: 'PLAYER_KICKED',
  PLAYER_DISCONNECTED: 'PLAYER_DISCONNECTED',
  PLAYER_RECONNECTED: 'PLAYER_RECONNECTED',
  AI_PLAYER_ADDED: 'AI_PLAYER_ADDED',
  AI_PLAYER_REMOVED: 'AI_PLAYER_REMOVED',
  AI_THINKING: 'AI_THINKING',
  AI_ACTION: 'AI_ACTION',
  AI_MESSAGE: 'AI_MESSAGE',
  RECEIVE_MESSAGE: 'RECEIVE_MESSAGE',
  GAME_STARTED: 'GAME_STARTED',
  CARDS_DEALT: 'CARDS_DEALT',
  TURN_UPDATE: 'TURN_UPDATE',
  ACTION_RESULT: 'ACTION_RESULT',
  GAME_STATE: 'GAME_STATE',
  GAME_OVER: 'GAME_OVER',
  RETURN_TO_LOBBY: 'RETURN_TO_LOBBY',
  ERROR: 'ERROR',
  PONG: 'PONG'  // Added for heartbeat
};

class WebSocketManager {
  constructor() {
    this.ws = null;
    this.messageHandlers = {};
    this.stateHandlers = [];
    this.isIntentionalDisconnect = false;
    this.reconnectTimeout = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.messageQueue = [];  // Queue messages when disconnected
    this.pingInterval = null;
    this.lastPongTime = Date.now();
    this.missedPongs = 0;  // Track consecutive missed pongs
    this.pingTimeoutMs = 30000;  // 30 seconds
    this.isReconnecting = false;
    this.rejoinTimeout = null;  // Track rejoin timeout
    this.rejoinCountdownInterval = null;  // Track countdown interval
    this.rejoinTimeRemaining = 0;  // Seconds remaining for rejoin
  }

  connect() {
    // Don't create a new connection if one exists and is open/connecting
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      console.log('WebSocket already connected or connecting');
      return Promise.resolve();
    }

    // Reset intentional disconnect flag when connecting
    this.isIntentionalDisconnect = false;
    const wasReconnecting = this.isReconnecting;
    this.isReconnecting = false;

    // Clear any existing reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    return new Promise((resolve, reject) => {
      try {
        console.log('Creating new WebSocket connection...');
        this.ws = new WebSocket(WS_URL);

        this.ws.onopen = () => {
          console.log('[WEBSOCKET] Connected successfully', {
            reconnectAttempts: this.reconnectAttempts,
            wasReconnecting,
            hasSession: !!(gameService.sessionID && gameService.roomCode),
            sessionID: gameService.sessionID ? `${gameService.sessionID.substring(0, 8)}...` : 'none',
            roomCode: gameService.roomCode || 'none'
          });

          this.reconnectAttempts = 0;
          this.isReconnecting = false;
          this.notifyStateHandlers({ isConnected: true, error: null, reconnecting: false });

          // Process queued messages
          this.processMessageQueue();

          // Start heartbeat
          this.startHeartbeat();

          // Check for existing session and rejoin (both on reconnection and initial load)
          const hasSession = gameService.sessionID && gameService.roomCode;
          if (hasSession) {
            if (wasReconnecting) {
              console.log('[WEBSOCKET] Connection restored after disconnect, attempting to rejoin session...');
            } else {
              console.log('[WEBSOCKET] Initial connection with existing session, attempting to rejoin...');
            }
            // Small delay to ensure connection is stable
            setTimeout(() => {
              this.checkAndRejoinSession();
            }, 200);
          } else {
            console.log('[WEBSOCKET] No existing session to rejoin');
          }

          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);

            // Handle pong for heartbeat (don't log to reduce noise)
            if (message.type === MSG_TYPES.PONG) {
              this.lastPongTime = Date.now();
              this.missedPongs = 0; // Reset missed pong counter
              return;
            }

            // Log all other messages
            console.log('Received message:', message);

            // Call registered handlers
            const handlers = this.messageHandlers[message.type] || [];
            handlers.forEach(handler => {
              try {
                handler(message);
              } catch (err) {
                console.error('Error in message handler:', err);
              }
            });

            // Handle common messages
            this.handleMessage(message);
          } catch (err) {
            console.error('Error parsing message:', err);
          }
        };

        this.ws.onerror = (event) => {
          console.error('WebSocket error:', event);
          this.notifyStateHandlers({ error: 'Connection error' });
          reject(new Error('WebSocket connection failed'));
        };

        this.ws.onclose = (event) => {
          const codeDescriptions = {
            1000: 'Normal closure',
            1001: 'Going away',
            1006: 'Abnormal closure (no close frame)',
            1008: 'Policy violation',
            1009: 'Message too big',
            1011: 'Server error'
          };
          const codeDesc = codeDescriptions[event.code] || 'Unknown';

          console.log(`[WEBSOCKET] Disconnected: ${event.code} (${codeDesc}) - ${event.reason || 'No reason'}`, {
            isIntentional: this.isIntentionalDisconnect,
            isReconnecting: this.isReconnecting,
            reconnectAttempts: this.reconnectAttempts,
            hasSession: !!(gameService.sessionID && gameService.roomCode)
          });

          this.ws = null;
          this.stopHeartbeat();

          // Only reconnect if it was not an intentional disconnect and we haven't exceeded max attempts
          if (!this.isIntentionalDisconnect &&
              !this.isReconnecting &&
              event.code !== 1000 &&
              event.code !== 1001 &&
              this.reconnectAttempts < this.maxReconnectAttempts) {
            this.isReconnecting = true;
            this.reconnectAttempts++;
            const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000); // Exponential backoff
            console.log(`[WEBSOCKET] Scheduling reconnect in ${delay}ms... (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

            // Notify handlers about reconnecting state
            this.notifyStateHandlers({
              isConnected: false,
              reconnecting: true,
              reconnectAttempt: this.reconnectAttempts,
              reconnectDelay: delay
            });

            this.reconnectTimeout = setTimeout(() => {
              console.log(`[WEBSOCKET] Reconnect attempt ${this.reconnectAttempts} starting...`);
              this.connect();
            }, delay);
          } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            // Max reconnection attempts reached
            console.error('[WEBSOCKET] Max reconnection attempts reached');
            this.notifyStateHandlers({
              isConnected: false,
              reconnecting: false,
              error: 'Connection lost. Please refresh the page.'
            });
          } else {
            // Intentional disconnect or normal close
            const reason = this.isIntentionalDisconnect ? 'Intentional disconnect' : 'Normal close';
            console.log(`[WEBSOCKET] ${reason}, not reconnecting`);
            this.notifyStateHandlers({ isConnected: false, reconnecting: false });
          }
        };

        // Set a timeout for initial connection
        setTimeout(() => {
          if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
            this.ws.close();
            reject(new Error('Connection timeout'));
          }
        }, 10000);
      } catch (err) {
        console.error('Error creating WebSocket:', err);
        this.notifyStateHandlers({ error: 'Failed to connect' });
        reject(err);
      }
    });
  }

  startHeartbeat() {
    this.stopHeartbeat();
    this.lastPongTime = Date.now();
    this.missedPongs = 0;

    this.pingInterval = setInterval(() => {
      // Don't send pings if not connected
      if (!this.isConnected()) {
        console.warn('[HEARTBEAT] Not connected, skipping ping');
        return;
      }

      // Check if we've received a pong recently
      const timeSinceLastPong = Date.now() - this.lastPongTime;

      if (timeSinceLastPong > this.pingTimeoutMs) {
        this.missedPongs++;
        console.warn(`[HEARTBEAT] Missed pong #${this.missedPongs} (${timeSinceLastPong}ms since last pong)`);

        // Only disconnect after missing 3 consecutive pongs (90 seconds of no response)
        if (this.missedPongs >= 3) {
          console.error('[HEARTBEAT] No pong received after 3 attempts, forcing disconnect...');
          this.ws?.close();
          return;
        }
      } else {
        // Pong was received in time, log for debugging
        if (this.missedPongs > 0) {
          console.log(`[HEARTBEAT] Pong received, resetting counter (was ${this.missedPongs})`);
          this.missedPongs = 0;
        }
      }

      // Send ping
      const sent = this.sendMessage(MSG_TYPES.PING, {});
      if (!sent) {
        console.warn('[HEARTBEAT] Failed to send ping - connection may be unstable');
      }
    }, this.pingTimeoutMs);
  }

  stopHeartbeat() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  disconnect() {
    this.isIntentionalDisconnect = true;
    this.stopHeartbeat();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.ws) {
      this.ws.close(1000, 'Intentional disconnect');
      this.ws = null;
    }
    this.messageQueue = [];  // Clear queue on intentional disconnect
    this.notifyStateHandlers({ isConnected: false });
  }

  sendMessage(type, data = {}) {
    const message = {
      type,
      playerId: gameService.playerID,
      data: data
    };

    // If not connected, queue the message (except for pings)
    if ((!this.ws || this.ws.readyState !== WebSocket.OPEN) && type !== MSG_TYPES.PING) {
      console.log('WebSocket not connected, queueing message:', message);
      this.messageQueue.push(message);
      this.notifyStateHandlers({ error: 'Not connected to server, message queued' });
      // Attempt to reconnect if not already trying
      if (!this.isReconnecting && !this.isIntentionalDisconnect) {
        this.connect();
      }
      return false;
    }

    try {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify(message));
        // Don't log PING messages to reduce console noise
        if (type !== MSG_TYPES.PING) {
          console.log('Sent message:', message);
        }
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error sending message:', err);
      this.notifyStateHandlers({ error: 'Failed to send message' });
      return false;
    }
  }

  processMessageQueue() {
    if (this.messageQueue.length > 0) {
      console.log(`Processing ${this.messageQueue.length} queued messages`);
      const queue = [...this.messageQueue];
      this.messageQueue = [];

      queue.forEach(message => {
        try {
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(message));
            console.log('Sent queued message:', message);
          }
        } catch (err) {
          console.error('Error sending queued message:', err);
        }
      });
    }
  }

  onMessage(type, handler) {
    if (!this.messageHandlers[type]) {
      this.messageHandlers[type] = [];
    }
    this.messageHandlers[type].push(handler);

    // Return cleanup function
    return () => {
      this.messageHandlers[type] = this.messageHandlers[type].filter(h => h !== handler);
    };
  }

  onStateChange(handler) {
    this.stateHandlers.push(handler);

    // Immediately notify with current state so components don't miss initial state
    handler({
      isConnected: this.isConnected(),
      reconnecting: this.isReconnecting,
      reconnectAttempt: this.reconnectAttempts,
      error: null
    });

    // Return cleanup function
    return () => {
      this.stateHandlers = this.stateHandlers.filter(h => h !== handler);
    };
  }

  notifyStateHandlers(state) {
    this.stateHandlers.forEach(handler => {
      try {
        handler(state);
      } catch (err) {
        console.error('Error in state handler:', err);
      }
    });
  }

  handleMessage(message) {
    switch (message.type) {
      case MSG_TYPES.SESSION_CREATED:
        if (message.data) {
          console.log('Session created:', message.data.sessionId);
          // Only set session ID if we don't already have one (e.g., from URL)
          if (!gameService.sessionID) {
            gameService.setSessionID(message.data.sessionId);
            console.log('Set new session ID:', message.data.sessionId);
          } else {
            console.log('Keeping existing session ID:', gameService.sessionID);
          }
          // Store playerID but don't overwrite playerName/roomCode yet
          gameService.playerID = message.data.playerId;
        }
        break;

      case MSG_TYPES.ROOM_CREATED:
        if (message.data) {
          gameService.setPlayerInfo(message.data.playerId, gameService.playerName, message.data.roomCode);

          // Cancel rejoin timeout on successful room join/rejoin
          if (this.rejoinTimeout) {
            console.log('[REJOIN] Success! Room joined/rejoined');
            this.cancelRejoinTimeout();
            this.notifyStateHandlers({
              rejoining: false,
              rejoinTimeRemaining: 0,
              error: null
            });
          }
        }
        break;

      case MSG_TYPES.PLAYER_JOINED:
        if (message.data) {
          gameService.updateGameState({
            players: message.data.players,
            isCreator: message.data.isCreator
          });
        }
        break;

      case MSG_TYPES.PLAYER_DISCONNECTED:
        console.log('Player disconnected:', message.data);
        // Could update UI to show disconnected players
        break;

      case MSG_TYPES.PLAYER_RECONNECTED:
        console.log('Player reconnected:', message.data);
        // Could update UI to show reconnected players
        break;

      case MSG_TYPES.GAME_STARTED:
        console.log('Game started');
        break;

      case MSG_TYPES.CARDS_DEALT:
        if (message.data) {
          gameService.updateGameState({
            dealtCards: message.data.cards,
            phase: 'selecting'
          });
        }
        break;

      case MSG_TYPES.GAME_STATE:
        if (message.data) {
          gameService.updateGameState({
            players: message.data.players,
            currentPlayer: message.data.currentPlayer,
            deckCount: message.data.deckCount,
            yourCards: message.data.yourCards,
            yourLifeCards: message.data.yourLifeCards,
            yourDefense: message.data.yourDefense,
            yourLife: message.data.yourLife,
            phase: 'playing'
          });
        }
        break;

      case MSG_TYPES.ACTION_RESULT:
        if (message.data) {
          gameService.updateGameState({
            lastAction: message.data
          });
        }
        break;

      case MSG_TYPES.GAME_OVER:
        if (message.data) {
          gameService.updateGameState({
            phase: 'gameover',
            winner: message.data.winner,
            finalPlayers: message.data.players
          });
        }
        break;

      case MSG_TYPES.RETURN_TO_LOBBY:
        console.log('Returning to lobby');
        // Reset game state but keep player/room info
        gameService.updateGameState({
          phase: 'lobby',
          dealtCards: null,
          currentPlayer: null,
          deckCount: 0,
          yourCards: [],
          yourLifeCards: [],
          yourDefense: null,
          yourLife: 0,
          winner: null,
          finalPlayers: null,
          lastAction: null
        });
        break;

      case MSG_TYPES.ERROR:
        this.notifyStateHandlers({ error: message.error || 'An error occurred' });
        break;

      default:
        break;
    }
  }

  // Game action methods
  createRoom(playerName) {
    gameService.playerName = playerName;
    return this.sendMessage(MSG_TYPES.CREATE_ROOM, { playerName });
  }

  joinRoom(roomCode, playerName) {
    gameService.playerName = playerName;
    return this.sendMessage(MSG_TYPES.JOIN_ROOM, { roomCode, playerName });
  }

  startGame() {
    return this.sendMessage(MSG_TYPES.START_GAME);
  }

  selectCards(lifeCards, defenseCard) {
    return this.sendMessage(MSG_TYPES.SELECT_CARDS, {
      lifeCards,
      defenseCard
    });
  }

  attack(targetPlayerId) {
    return this.sendMessage(MSG_TYPES.ATTACK, { targetPlayerId });
  }

  changeDefense(targetPlayerId) {
    return this.sendMessage(MSG_TYPES.CHANGE_DEFENSE, { targetPlayerId });
  }

  leaveRoom() {
    return this.sendMessage(MSG_TYPES.LEAVE_ROOM);
  }

  kickPlayer(playerId) {
    return this.sendMessage(MSG_TYPES.KICK_PLAYER, { playerId });
  }

  addAIPlayer() {
    return this.sendMessage(MSG_TYPES.ADD_AI_PLAYER);
  }

  removeAIPlayer(aiPlayerId) {
    return this.sendMessage(MSG_TYPES.REMOVE_AI_PLAYER, { playerId: aiPlayerId });
  }

  sendPlayerMessage(recipientId, text) {
    return this.sendMessage(MSG_TYPES.SEND_MESSAGE, {
      to: recipientId,
      text: text
    });
  }

  rejoinSession(sessionID, roomCode) {
    return this.sendMessage(MSG_TYPES.REJOIN_SESSION, {
      sessionId: sessionID,
      roomCode: roomCode
    });
  }

  // Get connection status
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  // Check for existing session and attempt to rejoin
  checkAndRejoinSession() {
    const sessionID = gameService.sessionID;
    const roomCode = gameService.roomCode;

    if (sessionID && sessionID.trim() && roomCode && roomCode.trim()) {
      console.log('[WEBSOCKET] Found existing session, attempting to rejoin...', {
        sessionID,
        roomCode,
        isConnected: this.isConnected()
      });

      // Only proceed if we're connected
      if (!this.isConnected()) {
        console.warn('[WEBSOCKET] Not connected yet, will retry rejoin');
        return false;
      }

      // Update URL to maintain session on reconnect (using replaceState to avoid history pollution)
      try {
        const params = new URLSearchParams();
        params.set('room', roomCode);
        params.set('session', sessionID);
        const newURL = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, '', newURL);
      } catch (err) {
        console.error('[WEBSOCKET] Error updating URL:', err);
      }

      // Start 10-second rejoin timeout with countdown
      this.startRejoinTimeout();

      // Wait a moment for connection to stabilize, then send rejoin request
      setTimeout(() => {
        console.log('[WEBSOCKET] Sending rejoin request...', { sessionID, roomCode });
        const sent = this.rejoinSession(sessionID, roomCode);
        if (!sent) {
          console.error('[WEBSOCKET] Failed to send rejoin message - connection may be lost');
          this.cancelRejoinTimeout();
          this.handleRejoinFailure();
        }
      }, 100);
      return true;
    } else {
      console.log('[WEBSOCKET] No valid session to rejoin', { sessionID, roomCode });
      return false;
    }
  }

  startRejoinTimeout() {
    // Clear any existing timeout
    this.cancelRejoinTimeout();

    // Set initial time remaining
    this.rejoinTimeRemaining = 10;

    // Notify handlers about rejoin in progress
    this.notifyStateHandlers({
      rejoining: true,
      rejoinTimeRemaining: this.rejoinTimeRemaining
    });

    // Start countdown interval (update every second)
    this.rejoinCountdownInterval = setInterval(() => {
      this.rejoinTimeRemaining--;

      console.log(`[REJOIN] Time remaining: ${this.rejoinTimeRemaining}s`);

      this.notifyStateHandlers({
        rejoining: true,
        rejoinTimeRemaining: this.rejoinTimeRemaining
      });

      if (this.rejoinTimeRemaining <= 0) {
        this.cancelRejoinTimeout();
      }
    }, 1000);

    // Set timeout for failure (10 seconds)
    this.rejoinTimeout = setTimeout(() => {
      console.error('[REJOIN] Timeout: Failed to rejoin within 10 seconds');
      this.cancelRejoinTimeout();
      this.handleRejoinFailure();
    }, 10000);
  }

  cancelRejoinTimeout() {
    if (this.rejoinTimeout) {
      clearTimeout(this.rejoinTimeout);
      this.rejoinTimeout = null;
    }
    if (this.rejoinCountdownInterval) {
      clearInterval(this.rejoinCountdownInterval);
      this.rejoinCountdownInterval = null;
    }
    this.rejoinTimeRemaining = 0;
  }

  handleRejoinFailure() {
    console.log('[REJOIN] Cleaning up failed session...');

    // Clear session data
    gameService.clearSession();

    // Clear URL parameters
    try {
      const newURL = window.location.pathname;
      window.history.replaceState({}, '', newURL);
    } catch (err) {
      console.error('[REJOIN] Error clearing URL:', err);
    }

    // Notify handlers
    this.notifyStateHandlers({
      rejoining: false,
      rejoinTimeRemaining: 0,
      error: 'Failed to restore session. Please create or join a room again.'
    });
  }
}

// Create a singleton instance
const wsManager = new WebSocketManager();

// Auto-connect on module load
if (typeof window !== 'undefined') {
  wsManager.connect().catch(err => {
    console.error('Initial connection failed:', err);
  });
}

// Export both the instance and message types
export { wsManager, MSG_TYPES };
export default wsManager;